import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import jobService from '../../services/jobService';
import applicationService from '../../services/applicationService';
import './ApplyJob.css';

const ApplyJob = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const inDashboard = isAuthenticated && user?.role === 'candidate';
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [formData, setFormData] = useState({
    coverLetter: '',
    resume: '',
    expectedSalary: '',
    availableFrom: '',
    answers: []
  });

  useEffect(() => {
    fetchJobDetails();
  }, [id]);


  const formatLocation = (loc) => {
    if (!loc) return '';
    const city = typeof loc.city === 'object' ? loc.city?.name : loc.city;
    return [city, loc.state, loc.country, loc.address].filter(Boolean).join(', ');
  };

  const fetchJobDetails = async () => {
    try {
      const response = await jobService.getJobById(id);
      setJob(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Failed to fetch job details');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const jobLocation = formatLocation(job?.location);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file only');
      e.target.value = '';
      setResumeFile(null);
      return;
    }
    setResumeFile(file || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);

      const applicationData = new FormData();
      applicationData.append('jobId', id);
      applicationData.append('coverLetter', formData.coverLetter || '');
      applicationData.append('expectedSalary', formData.expectedSalary ? parseInt(formData.expectedSalary) : '');
      applicationData.append('availableFrom', formData.availableFrom || '');
      applicationData.append('answers', JSON.stringify(formData.answers || []));
      if (resumeFile) {
        applicationData.append('resume', resumeFile);
      }

      await applicationService.applyForJob(applicationData);
      toast.success('Application submitted successfully!');
      navigate('/candidate/my-applications');
    } catch (error) {
      console.error('Error submitting application:', error);
      const message = error?.error?.message || error?.message || 'Failed to submit application';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="public-apply-job-loading">
        <div className="aj-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className={`public-apply-job${inDashboard ? ' in-dashboard' : ''}`}>
        <div className="aj-empty">
          <span className="material-symbols-outlined">work_off</span>
          <h3>Job not found</h3>
          <p>This job may have been removed or expired.</p>
          <button type="button" className="aj-empty-btn" onClick={() => navigate('/jobs')}>
            <span className="material-symbols-outlined">search</span>
            Browse Jobs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`public-apply-job${inDashboard ? ' in-dashboard' : ''}`}>
      <Link to={`/jobs/${id}`} className="aj-back-link">
        <span className="material-symbols-outlined">arrow_back</span>
        Back to Job
      </Link>

      {/* Header */}
      <div className="aj-header">
        <div className="aj-header-logo">
          {job.company?.logo
            ? <img src={job.company.logo} alt={job.company.name} />
            : (job.company?.name?.charAt(0) || 'C')}
        </div>
        <div className="aj-header-info">
          <h1>Apply — {job.title}</h1>
          <p>{job.company?.name}</p>
        </div>
      </div>

      <div className="aj-layout">
        <div className="aj-main">

      {/* Application form */}
      <div className="aj-form-card">
        <h3 className="aj-form-section-title">
          <span className="material-symbols-outlined">edit_note</span>
          Application Details
        </h3>
        <form onSubmit={handleSubmit} className="aj-form">
          <div className="aj-field">
            <label className="aj-label">Cover Letter <small>Optional</small></label>
            <textarea
              name="coverLetter"
              value={formData.coverLetter}
              onChange={handleChange}
              rows={8}
              placeholder="Tell us why you're a great fit for this position. Mention your key skills, relevant experience, and why you're excited about this role..."
              className="aj-textarea"
            />
            <span className="aj-hint">Be specific about your relevant experience.</span>
          </div>

          <div className="aj-form-row">
            <div className="aj-field">
              <label className="aj-label">Resume / CV <small>PDF only</small></label>
              <input
                type="file"
                name="resume"
                accept=".pdf,application/pdf"
                onChange={handleFileChange}
                className="aj-input"
              />
              <span className="aj-hint">Upload your resume as a PDF file (max 10MB)</span>
              {resumeFile && (
                <span className="aj-file-name">Selected: {resumeFile.name}</span>
              )}
            </div>
            <div className="aj-field">
              <label className="aj-label">Expected Salary <small>Optional</small></label>
              <input
                type="number"
                name="expectedSalary"
                value={formData.expectedSalary}
                onChange={handleChange}
                placeholder="Annual salary expectation"
                className="aj-input"
              />
              <span className="aj-hint">Helps the employer understand your expectations</span>
            </div>
          </div>

          <div className="aj-field">
            <label className="aj-label">Available From <small>Optional</small></label>
            <input
              type="date"
              name="availableFrom"
              value={formData.availableFrom}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              className="aj-input"
            />
            <span className="aj-hint">When can you start if selected?</span>
          </div>

          {job.questions && job.questions.length > 0 && (
            <>
              <h4 className="aj-form-section-title">
                <span className="material-symbols-outlined">quiz</span>
                Additional Questions
              </h4>
              {job.questions.map((question, index) => (
                <div key={index} className="aj-field">
                  <label className="aj-label">{question}</label>
                  <textarea
                    rows={4}
                    placeholder="Your answer..."
                    className="aj-textarea"
                    onChange={(e) => {
                      const newAnswers = [...formData.answers];
                      newAnswers[index] = { question, answer: e.target.value };
                      setFormData({ ...formData, answers: newAnswers });
                    }}
                  />
                </div>
              ))}
            </>
          )}

          <label className="aj-checkbox-label">
            <input type="checkbox" required />
            I confirm that all information is accurate and I agree to the <a href="/terms" target="_blank">Terms and Conditions</a>.
          </label>

          <div className="aj-form-actions">
            <button type="button" className="aj-cancel-btn" onClick={() => navigate(`/jobs/${id}`)} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="aj-submit-btn" disabled={submitting}>
              {submitting ? (
                <><div className="aj-submit-spinner"></div> Submitting…</>
              ) : (
                <><span className="material-symbols-outlined">send</span> Submit Application</>
              )}
            </button>
          </div>
        </form>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="aj-sidebar">
          <div className="aj-side-card">
            <p className="aj-side-title">Job Overview</p>
            <div className="aj-overview-list">
              <div className="aj-overview-item">
                <div className="aj-overview-icon"><span className="material-symbols-outlined">location_on</span></div>
                <div>
                  <span className="aj-overview-label">Location</span>
                  <span className="aj-overview-value">{jobLocation || 'Remote'}</span>
                </div>
              </div>
              {job.jobType?.name && (
                <div className="aj-overview-item">
                  <div className="aj-overview-icon"><span className="material-symbols-outlined">work</span></div>
                  <div>
                    <span className="aj-overview-label">Job Type</span>
                    <span className="aj-overview-value">{job.jobType.name}</span>
                  </div>
                </div>
              )}
              {job.workMode && (
                <div className="aj-overview-item">
                  <div className="aj-overview-icon"><span className="material-symbols-outlined">business_center</span></div>
                  <div>
                    <span className="aj-overview-label">Work Mode</span>
                    <span className="aj-overview-value">{job.workMode}</span>
                  </div>
                </div>
              )}
              {job.salary?.min && (
                <div className="aj-overview-item">
                  <div className="aj-overview-icon"><span className="material-symbols-outlined">currency_rupee</span></div>
                  <div>
                    <span className="aj-overview-label">Salary</span>
                    <span className="aj-overview-value">
                      {job.salary.currency} {job.salary.min.toLocaleString()}
                      {job.salary.max ? ` – ${job.salary.max.toLocaleString()}` : ''} / {job.salary.period}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="aj-side-card">
            <p className="aj-side-title">Your Information</p>
            <div className="aj-user-info">
              <div className="aj-info-item">
                <span className="aj-info-label">Full Name</span>
                <span className="aj-info-value">{user?.name}</span>
              </div>
              <div className="aj-info-item">
                <span className="aj-info-label">Email</span>
                <span className="aj-info-value">{user?.email}</span>
              </div>
              {user?.phone && (
                <div className="aj-info-item">
                  <span className="aj-info-label">Phone</span>
                  <span className="aj-info-value">{user.phone}</span>
                </div>
              )}
            </div>
          </div>

          <div className="aj-tips">
            <h4 className="aj-tips-title">
              <span className="material-symbols-outlined">tips_and_updates</span>
              Application Tips
            </h4>
            <ul>
              <li>Tailor your cover letter to this specific role and company</li>
              <li>Highlight relevant skills and measurable results</li>
              <li>Proofread carefully before submitting</li>
              <li>Keep your resume updated with your latest experience</li>
              <li>Be realistic about salary expectations and availability</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ApplyJob;
